import { Injectable, inject } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthStore } from '@root/app/store/auth.store';
import { environment } from '@root/environments/environment';

@Injectable()
export class AuthorizeInterceptor implements HttpInterceptor {
  private readonly authStore = inject(AuthStore);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // This interceptor authenticates calls to OUR API gateway only. Everything else —
    // static assets (e.g. /assets/i18n/*.json), Google Maps, any third-party URL — must
    // pass through untouched. Stamping `Authorization: Bearer undefined` + `Api-Version: v1`
    // onto the static i18n request made the deployed gateway reject it on a cold (uncached)
    // load, so the bootstrap translation fetch failed, was swallowed by the initializer's
    // catch, and the UI rendered raw translation keys until a refresh served the JSON from
    // browser cache. Only requests targeting environment.api.url get the auth/version headers.
    if (!this.isApiRequest(request.url)) {
      return next.handle(request);
    }

    // Login is fully public (no token exists yet) — version header only. It STILL
    // needs Api-Version or the gateway 404s "Resource not found".
    if (this.isPublicAuthEndpoint(request.url)) {
      return next.handle(this.withVersionHeader(request));
    }

    if (this.isTokenBearingAuthEndpoint(request.url)) {
      return next.handle(this.withAuthHeaders(request, this.authStore.authInfo().access_token));
    }

    const accessToken = this.authStore.authInfo().access_token;

    // PROACTIVE: refresh before sending if the token is expired / about to expire,
    // so the burst never produces failed requests in the first place.
    if (accessToken && this.authStore.isAccessTokenExpired()) {
      return this.refreshAndRetry(request, next);
    }

    return next
      .handle(this.withAuthHeaders(request, accessToken))
      .pipe(catchError((error: any) => this.handleError(error, request, next)));
  }

  private handleError(error: any, request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (error.status !== 401) return throwError(() => error);
    if (error.url?.includes('logout')) return EMPTY;
    if (error.url?.includes('refresh')) {
      // Defensive: a 401 from the refresh endpoint is terminal -> bounce to login.
      this.authStore.logout();
      return EMPTY;
    }
    // REACTIVE fallback: a 401 that slipped past the proactive check — but ONLY when the
    // token is actually expired. A 401 with a still-valid token is a genuine authorization
    // failure (insufficient permissions / wrong partner context); refreshing cannot fix it
    // and a blind refresh+retry would loop refresh->401->refresh. Because a refresh always
    // yields a fresh token, this also bounds the whole reactive path to a single refresh.
    if (!this.authStore.isAccessTokenExpired()) {
      return throwError(() => error);
    }
    return this.refreshAndRetry(request, next);
  }

  /**
   * Wait for the single shared refresh (owned by AuthStore, so the interceptor and the
   * auth guard share one in-flight refresh), then retry this request with the fresh
   * token. AuthStore.ensureFreshToken() handles the SSO-vs-password decision and logs
   * out on terminal failure.
   */
  private refreshAndRetry(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    return this.authStore
      .ensureFreshToken()
      .pipe(switchMap(token => next.handle(this.withAuthHeaders(request, token))));
  }

  /** Only requests to our API gateway carry auth/version headers; assets & 3rd parties don't. */
  private isApiRequest(url: string): boolean {
    return url.startsWith(environment.api.url);
  }

  private isPublicAuthEndpoint(url: string): boolean {
    return url.includes('/me/login');
  }

  /**
   * Logout & refresh: auth endpoints that still need the Bearer (the backend extracts
   * oid/iss from it) but must skip the refresh machinery to avoid recursion.
   */
  private isTokenBearingAuthEndpoint(url: string): boolean {
    return url.includes('/token/refresh') || url.includes('/me/logout');
  }

  /** The API gateway routes on Api-Version; every request needs it, auth endpoints included. */
  private withVersionHeader(request: HttpRequest<any>): HttpRequest<any> {
    return request.clone({ setHeaders: { 'Api-Version': 'v1' } });
  }

  private withAuthHeaders(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: { 'Api-Version': 'v1', Authorization: `Bearer ${token}` },
    });
  }
}
